import React, { useEffect, useState } from 'react';
import Brand, { BrandMark } from '../Layout/Brand';
import SeoHead from './SeoHead';
import { trackWebsiteEvent } from '../../services/siteAnalytics';
import '../../styles/marketing.css';

const faqs = [
  ['Is LeaveNOW another navigation app?', 'No. LeaveNOW determines when you should leave, then opens your preferred navigation app for the route.'],
  ['What happens when traffic changes?', 'LeaveNOW Live is designed to refresh active trips and update your departure recommendation without making you repeatedly reopen a maps app.'],
  ['Which navigation apps work with LeaveNOW?', 'You can choose Apple Maps, Google Maps, or Waze as your preferred handoff.'],
  ['Does LeaveNOW work for everyday commutes?', 'Yes. It is built for daily commutes, appointments, school runs, airport trips, and anything else with an arrival time.'],
  ['Are live flight and train schedules available?', 'The trip-planning foundation is being built now. Live schedule integrations are planned for LeaveNOW Live and are not yet enabled.', 'Coming Soon'],
];

const liveStates = [
  { key: 'on-time', label: 'On time', kicker: 'LEAVE IN', value: '18:42', detail: 'Office · 34 min drive', arrival: 'Arrive by 8:30 AM', badge: 'ON TIME' },
  { key: 'changing', label: 'Traffic changed', kicker: 'NEW RECOMMENDATION', value: 'Leave by 7:42 AM', detail: 'Traffic added 8 minutes', arrival: 'Arrive by 8:30 AM', badge: 'TRAFFIC CHANGED' },
  { key: 'leave-now', label: 'Leave now', kicker: 'TIME TO GO', value: 'LEAVE NOW', detail: 'Office · 34 min drive', arrival: 'Arrive by 8:30 AM', badge: 'ACTION NEEDED' },
];

export default function MarketingSite({ user }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [liveState, setLiveState] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setLiveState((current) => (current + 1) % liveStates.length), 3600);
    return () => window.clearInterval(timer);
  }, []);
  const currentLiveState = liveStates[liveState];
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(([question, answer]) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };
  return (
    <div className="marketing-site">
      <SeoHead
        title="When Should I Leave? Traffic-Aware Departure Alerts | LeaveNOW"
        description="Enter when you need to arrive. LeaveNOW calculates what time to leave, monitors changing traffic, alerts you when it’s time to go, and opens your preferred maps app."
        schema={faqSchema}
      />
      <header className="marketing-header">
        <a href="/" className="marketing-brand" aria-label="LeaveNOW home"><Brand /></a>
        <button className="mobile-menu-button" onClick={() => setMenuOpen((value) => !value)} aria-expanded={menuOpen} aria-label="Toggle navigation">Menu</button>
        <nav className={menuOpen ? 'open' : ''} aria-label="Main navigation">
          <a href="#how">How it works</a>
          <a href="#features">Features</a>
          <a href="#pricing">Pricing</a>
          <a href="#faq">FAQ</a>
          <a href="#contact">Contact</a>
          <a href="/plans" className="nav-primary">Choose a plan</a>
        </nav>
      </header>

      <main>
        <section className="marketing-hero">
          <div className="hero-content">
            <p className="marketing-eyebrow">Know When to Go.</p>
            <h1>Know exactly when to leave.<br /><span>Stop checking traffic.</span></h1>
            <p className="hero-lede">Set when you need to arrive. LeaveNOW works backward, watches the trip, and tells you when it is time to go.</p>
            <div className="hero-actions">
              <a className="marketing-primary" href="/plans" onClick={() => trackWebsiteEvent('cta_click', { cta: 'get_leavenow', location: 'hero' })}>Get LeaveNOW</a>
              <a className="marketing-link" href="#how">See how it works <span>↓</span></a>
            </div>
            <p className="hero-trust">LeaveNOW handles <strong>when</strong>. Your preferred maps app handles <strong>where</strong>.</p>
          </div>
          <div className="departure-visual" aria-label="Example LeaveNOW departure card">
            <div className="visual-glow" />
            <div className="phone-card">
              <div className="phone-top"><BrandMark size="sm" /><strong>Leave<span>NOW</span></strong><em>ON TIME</em></div>
              <p>DESTINATION</p>
              <h2>Morning commute</h2>
              <div className="phone-divider" />
              <div className="departure-grid">
                <div><small>LEAVE IN</small><strong>18:42</strong></div>
                <div><small>ETA</small><strong>34 min</strong><span>Arrive 8:30 AM</span></div>
              </div>
            </div>
            <div className="traffic-shift"><span>Traffic changed</span><strong>Leave 6 minutes earlier</strong></div>
          </div>
        </section>

        <section className="anxiety-strip" aria-label="LeaveNOW benefits">
          <span>Less checking.</span><span>Less guessing.</span><span>Less rushing.</span>
        </section>

        <section className="marketing-section how-section" id="how">
          <div className="section-intro">
            <p className="marketing-eyebrow">Plan once</p>
            <h2>Your arrival time becomes a calm plan.</h2>
            <p>You already know where and when. LeaveNOW turns that into the moment you need to walk out the door.</p>
          </div>
          <div className="steps-grid">
            {[
              ['01', 'Choose where and when', 'Enter your destination and the time you need to arrive.'],
              ['02', 'Get your departure time', 'LeaveNOW calculates the travel time and works backward.'],
              ['03', 'Stay ahead of changes', 'Live monitoring can adjust the recommendation as traffic moves.'],
              ['04', 'Tap once and go', 'The LeaveNOW alert opens your preferred navigation app.'],
            ].map(([number, title, body]) => <article className="step-card" key={number}><span>{number}</span><h3>{title}</h3><p>{body}</p></article>)}
          </div>
        </section>

        <section className="marketing-section feature-section" id="features">
          <div className="feature-copy">
            <p className="marketing-eyebrow">The glanceable trip</p>
            <h2>The answer stays on your Lock Screen.</h2>
            <p>No repeated traffic checks. Your active trip can show the current countdown, ETA, arrival time, and changing departure recommendation where you already look.</p>
            <ul>
              <li><span>✓</span> Lock Screen Live Activity</li>
              <li><span>✓</span> Dynamic Island countdown</li>
              <li><span>✓</span> Calm, time-sensitive reminders</li>
              <li><span>✓</span> Apple Maps, Google Maps, or Waze</li>
            </ul>
          </div>
          <div className={`iphone-demo state-${currentLiveState.key}`}>
            <div className="iphone-island" />
            <div className="iphone-status"><span>8:11</span><span>● ● ◒</span></div>
            <div className="lock-time">8:11</div>
            <div className="lock-date">Tuesday, July 28</div>
            <div className="live-alert-preview">
              <div><BrandMark size="xs" /><strong>Leave<span>NOW</span></strong><b>{currentLiveState.badge}</b></div>
              <small>{currentLiveState.kicker}</small>
              <strong className="live-count">{currentLiveState.value}</strong>
              <p>{currentLiveState.detail}</p>
              <p className="arrival-promise">{currentLiveState.arrival}</p>
            </div>
            <div className="iphone-home-indicator" />
          </div>
          <div className="alert-state-picker" aria-label="Preview Live alert states">
            {liveStates.map((state, index) => (
              <button type="button" className={index === liveState ? 'active' : ''} onClick={() => setLiveState(index)} key={state.key} aria-pressed={index === liveState}>
                <span className={`state-dot ${state.key}`} />{state.label}
              </button>
            ))}
          </div>
        </section>

        <section className="marketing-section product-tour" aria-labelledby="product-tour-title">
          <div className="section-intro centered">
            <p className="marketing-eyebrow">The LeaveNOW experience</p>
            <h2 id="product-tour-title">Plan it. Watch it. Go.</h2>
            <p>Three calm screens carry a trip from an arrival time to the moment you walk out the door.</p>
          </div>
          <div className="product-screens">
            <article>
              <div className="mini-iphone plan-screen">
                <div className="mini-island" />
                <div className="app-screen-head"><BrandMark size="xs" /><strong>Leave<span>NOW</span></strong></div>
                <small>NEW TRIP</small><h3>Where do you need to be?</h3>
                <div className="screen-field">Office</div><div className="screen-field">Arrive by 8:30 AM</div>
                <div className="screen-button">Calculate departure</div>
              </div>
              <h3>Plan once</h3><p>Choose a destination and arrival time. LeaveNOW works backward.</p>
            </article>
            <article>
              <div className="mini-iphone monitor-screen">
                <div className="mini-island" />
                <div className="app-screen-head"><BrandMark size="xs" /><strong>Leave<span>NOW</span></strong></div>
                <small>DRIVING</small><h3>Morning commute</h3>
                <div className="route-map">
                  <img src="/sample-driving-route.jpg" alt="Sample driving route from Thomas Jefferson High School to Kennywood" />
                </div>
                <div className="route-address"><span>Thomas Jefferson High School</span><b>→</b><span>Kennywood</span></div>
                <div className="monitor-metric"><span>LEAVE IN</span><strong>18:42</strong></div>
                <div className="traffic-note">Traffic looks steady</div>
              </div>
              <h3>Live monitoring</h3><p>Color changes make a timing shift clear without creating noise.</p>
            </article>
            <article>
              <div className="mini-iphone action-screen">
                <div className="mini-island" />
                <div className="app-screen-head"><BrandMark size="xs" /><strong>Leave<span>NOW</span></strong></div>
                <small>TIME TO GO</small><h3>Leave now.</h3>
                <p>Office · 34 min<br />Arrive by 8:30 AM</p>
                <div className="screen-button">LEAVE NOW</div>
                <div className="screen-secondary">Snooze 5 min</div>
              </div>
              <h3>One tap and go</h3><p>The primary action opens your preferred navigation app immediately.</p>
            </article>
          </div>
        </section>

        <section className="marketing-section use-cases">
          <div className="section-intro">
            <p className="marketing-eyebrow">Built for real life</p>
            <h2>Every trip that has a “be there by.”</h2>
            <p>Use LeaveNOW as a traffic-aware departure time calculator for commutes, airport runs, appointments, school pickups, and everyday plans.</p>
          </div>
          <div className="case-grid">
            {[
              ['↗', 'Daily commute', 'Start the workday without negotiating with traffic.'],
              ['✈', 'Airport trips', 'Work backward from the time you need to be at the terminal.'],
              ['●', 'Appointments', 'Protect the time that matters without setting another generic alarm.'],
              ['⌁', 'Family schedules', 'Make school, practice, and pickups feel less frantic.'],
            ].map(([icon, title, body]) => <article key={title}><span>{icon}</span><h3>{title}</h3><p>{body}</p></article>)}
          </div>
          <nav className="seo-topic-links" aria-label="Departure planning guides">
            <a href="/when-should-i-leave">When should I leave?<span>→</span></a>
            <a href="/airport-leave-time">Airport leave-time guide<span>→</span></a>
            <a href="/commute-time-calculator">Commute calculator<span>→</span></a>
            <a href="/departure-reminder">Traffic-aware reminders<span>→</span></a>
            <a href="/arrive-on-time">How to arrive on time<span>→</span></a>
          </nav>
        </section>

        <section className="marketing-section pricing-section" id="pricing">
          <div className="section-intro centered">
            <p className="marketing-eyebrow">Simple plans</p>
            <h2>Plan for free. Go live when timing matters.</h2>
            <p>No surprise tiers. Start free, then add live traffic monitoring when changing conditions matter.</p>
          </div>
          <div className="pricing-grid">
            <article className="price-card">
              <p>LeaveNOW</p><h3>Free</h3><span>The planning essentials.</span>
              <ul><li>✓ Create and manage trips</li><li>✓ Core departure recommendations</li><li>✓ Scheduled departure reminders</li><li>✓ Dynamic Island and Lock Screen card</li><li>✓ Saved places and trip history</li><li>✓ One-tap navigation handoff</li></ul>
              <a href="/plans?plan=free" onClick={() => trackWebsiteEvent('plan_selected', { plan: 'free', location: 'pricing' })}>Choose Free</a>
            </article>
            <article className="price-card featured">
              <div className="popular-label">LIVE GUIDANCE</div>
              <p>LeaveNOW Live</p>
              <h3>$4.99 <small>/ month</small></h3>
              <span>Or $39.99 annually. Save 33%.</span>
              <ul><li>✓ Continuous trip monitoring</li><li>✓ Automatic traffic recalculation</li><li>✓ Updated departure recommendations</li><li>✓ Updated Lock Screen guidance</li><li className="future-feature"><span>Coming Soon</span> Live flight and train schedules</li></ul>
              <a href="/plans?plan=live" onClick={() => trackWebsiteEvent('plan_selected', { plan: 'live', location: 'pricing' })}>Go Live</a>
              <small className="price-note">Choose monthly or annual billing. Cancel anytime.</small>
            </article>
          </div>
        </section>

        <section className="marketing-section faq-section" id="faq">
          <div className="section-intro"><p className="marketing-eyebrow">Questions, answered</p><h2>Clarity before you go.</h2></div>
          <div className="faq-list">{faqs.map(([question, answer, status]) => <details key={question}><summary><span className="faq-question">{question}{status && <b className="coming-soon-badge">{status}</b>}</span><span>+</span></summary><p>{answer}</p></details>)}</div>
        </section>

        <section className="marketing-section contact-section" id="contact">
          <div className="section-intro centered">
            <p className="marketing-eyebrow">We’re here to help</p>
            <h2>Questions before you go?</h2>
            <p>For product help, account questions, or subscription support, email <a href="mailto:support@getleavenow.com" onClick={() => trackWebsiteEvent('support_clicked', { location: 'contact' })}>support@getleavenow.com</a>.</p>
          </div>
        </section>

        <section className="final-cta">
          <BrandMark size="lg" />
          <p className="marketing-eyebrow">Know When to Go.</p>
          <h2>Plan once. Leave at the right time.</h2>
          <a href="/plans">Choose your plan</a>
        </section>
      </main>

      <footer className="marketing-footer">
        <a href="/" aria-label="LeaveNOW home"><Brand /></a>
        <p>LeaveNOW determines when to leave. Your maps app guides the route.</p>
        <div><a href="#how">How it works</a><a href="#pricing">Pricing</a><a href="#faq">FAQ</a><a href="/plans">Get LeaveNOW</a><a href="/support">Support</a><a href="/privacy">Privacy</a></div>
        <small>© {new Date().getFullYear()} LeaveNOW. Know When to Go.</small>
      </footer>
    </div>
  );
}
